import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook personnalisé pour gérer un compte à rebours
 * @param {number} initialSeconds - Durée initiale en secondes
 * @param {function} onTimeUp - Callback appelé quand le timer atteint 0
 * @returns {{ timeLeft, isRunning, start, pause, reset }}
 */
function useTimer(initialSeconds, onTimeUp) {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);
  const onTimeUpRef = useRef(onTimeUp);

  // Toujours avoir la dernière version du callback
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  // Démarrer le timer
  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  // Mettre en pause
  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  // Réinitialiser
  const reset = useCallback((newSeconds) => {
    setIsRunning(false);
    setTimeLeft(newSeconds !== undefined ? newSeconds : initialSeconds);
  }, [initialSeconds]);

  // Logique du décompte
  useEffect(() => {
    if (!isRunning) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setIsRunning(false);
          // Appel du callback dans le prochain cycle
          setTimeout(() => {
            if (onTimeUpRef.current) onTimeUpRef.current();
          }, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning]);

  return { timeLeft, isRunning, start, pause, reset };
}

export default useTimer;
